/**
 * CartProvider — localStorage-backed shopping cart for the shop.
 * Item shape: { variant_id, product_id, product_slug, product_name, size_label, price_eur, weight_g, qty, image }
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const KEY = "gefion.shop.cart";
const CartCtx = createContext(null);

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const save = (items) => localStorage.setItem(KEY, JSON.stringify(items));

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);

  useEffect(() => { save(items); }, [items]);

  const add = useCallback((item) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.variant_id === item.variant_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + (item.qty || 1) };
        return next;
      }
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
  }, []);

  const setQty = useCallback((variant_id, qty) => {
    setItems((prev) => prev
      .map((x) => x.variant_id === variant_id ? { ...x, qty: Math.max(1, Math.min(999, qty)) } : x)
      .filter((x) => x.qty > 0));
  }, []);

  const remove = useCallback((variant_id) => {
    setItems((prev) => prev.filter((x) => x.variant_id !== variant_id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, x) => s + x.qty, 0);
  const subtotal = items.reduce((s, x) => s + x.price_eur * x.qty, 0);
  const totalWeightG = items.reduce((s, x) => s + x.weight_g * x.qty, 0);

  return (
    <CartCtx.Provider value={{ items, add, setQty, remove, clear, count, subtotal, totalWeightG }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx) || {
  items: [], add: () => {}, setQty: () => {}, remove: () => {}, clear: () => {},
  count: 0, subtotal: 0, totalWeightG: 0,
};
